import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { BarChart3, TrendingUp } from "lucide-react";
import { WalletConnectButton } from "@/components/WalletConnectButton";
import { Link } from "react-router-dom";

export const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background glow */}
      <div className="absolute inset-0 gradient-glow" />
      
      {/* Grid pattern overlay */}
      <div 
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `linear-gradient(hsl(var(--foreground)) 1px, transparent 1px),
                           linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }}
      />

      <div className="container relative z-10 px-4 py-20 md:py-32">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto text-center"
        >
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1, duration: 0.4 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border bg-card/50 backdrop-blur-sm mb-8"
          >
            <span className="flex h-2 w-2 rounded-full bg-primary animate-pulse" />
            <span className="text-sm text-muted-foreground">Hyperliquid Analytics</span>
          </motion.div>

          {/* Main heading */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6"
          >
            Are you profitable —
            <br />
            <span className="text-primary">or just lucky?</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10"
          >
            Connect your Hyperliquid wallet. Get your Sharpe ratio, drawdowns, and 
            real performance metrics. Know your edge.
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <WalletConnectButton />
            <Link to="/dashboard">
              <Button variant="outline-primary" size="lg">
                See Demo
              </Button>
            </Link>
          </motion.div>

          {/* Stats preview */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-3 gap-4"
          >
            <StatPreview 
              icon={<BarChart3 className="w-5 h-5 text-primary" />}
              label="Sharpe Ratio"
              value="1.84"
              subtext="Risk-adjusted returns"
            />
            <StatPreview 
              icon={<TrendingUp className="w-5 h-5 text-primary" />}
              label="Win Rate"
              value="62.4%"
              subtext="Based on 847 trades"
            />
            <StatPreview 
              icon={<BarChart3 className="w-5 h-5 text-destructive" />}
              label="Max Drawdown"
              value="-12.3%"
              subtext="Peak to trough"
            />
          </motion.div>
        </motion.div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
    </section>
  );
};

interface StatPreviewProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  subtext: string;
}

const StatPreview = ({ icon, label, value, subtext }: StatPreviewProps) => (
  <div className="stat-card">
    <div className="flex items-center gap-2 mb-2">
      {icon}
      <span className="text-sm text-muted-foreground">{label}</span>
    </div>
    <div className="text-3xl font-bold font-mono">{value}</div>
    <div className="text-xs text-muted-foreground mt-1">{subtext}</div>
  </div>
);
